import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { protocolDirectionLabel, protocolItemMeta } from "@/components/equipment/equipmentLabels";
import { Button } from "@/components/ui/button";
import { EQUIPMENT_PROTOCOL_HISTORY_PATH } from "@/hooks/useEquipmentProtocolHistory";
import { listEquipmentAssets, listEquipmentProtocols } from "@/lib/equipmentApi";
import { supabase } from "@/lib/supabase";
import type { EquipmentProtocol } from "@/types/equipment";

interface EquipmentRejectionBannerProps {
  orgId: string;
}

function HistoryLink({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <Button variant="outline" size="sm" className={className} asChild>
      <Link to={EQUIPMENT_PROTOCOL_HISTORY_PATH}>
        {t("admin.equipment.banner.fullHistory")}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}

export function EquipmentRejectionBanner({ orgId }: EquipmentRejectionBannerProps) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["equipment-rejections", orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const protocols = await listEquipmentProtocols({ orgId });
      const rejected = protocols
        .filter((item) => item.status === "rejected")
        .sort((a, b) => {
          const aTime = a.responded_at ?? a.initiated_at;
          const bTime = b.responded_at ?? b.initiated_at;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        })
        .slice(0, 3);
      if (rejected.length === 0) {
        return { rejected: [] as EquipmentProtocol[], names: {} as Record<string, string>, assets: [], keys: [] };
      }
      const workerIds = [...new Set(rejected.map((item) => item.worker_id))];
      const staffIds = [...new Set(rejected.map((item) => item.staff_equipment_id).filter(Boolean))] as string[];
      const [{ data: profiles }, assets, keysResult] = await Promise.all([
        supabase.from("profiles").select("id, full_name, email").in("id", workerIds),
        listEquipmentAssets(orgId),
        staffIds.length > 0
          ? supabase.from("staff_equipment").select("id, name").in("id", staffIds)
          : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      ]);
      const names: Record<string, string> = {};
      for (const profile of profiles ?? []) {
        names[profile.id] = profile.full_name || profile.email || "";
      }
      return { rejected, names, assets, keys: keysResult.data ?? [] };
    },
  });

  const rejected = data?.rejected ?? [];

  if (rejected.length === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
        <div>
          <h3 className="text-sm font-semibold">{t("admin.equipment.banner.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("admin.equipment.banner.noRejections")}</p>
        </div>
        <HistoryLink />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {t("admin.equipment.banner.rejectedTitle")}
        </h3>
        <HistoryLink />
      </div>
      <ul className="space-y-2">
        {rejected.map((protocol) => {
          const meta = protocolItemMeta(protocol, data?.assets ?? [], data?.keys ?? []);
          const workerName = data?.names[protocol.worker_id] || t("admin.equipment.workerFallback");
          return (
            <li key={protocol.id} className="rounded border border-border bg-background p-3 text-sm">
              <p className="font-medium">
                {t("admin.equipment.banner.rejectedLine", {
                  direction: protocolDirectionLabel(protocol.direction),
                  name: meta.name,
                })}
                {meta.serialNumber ? ` (${meta.serialNumber})` : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("admin.equipment.banner.workerLine", { name: workerName })}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("admin.equipment.banner.reasonInline", {
                  reason: protocol.rejection_reason?.trim() || t("admin.equipment.banner.noReason"),
                })}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
